/**
 * Every line the devices panel says (D171) — in one place, named by what
 * it answers, so the words are the design lane's to re-cut without
 * touching the wiring. The register is `account-copy.ts`'s: plain, second
 * person, the chrome's voice.
 *
 * @remarks
 * Two of these are not words but readings: {@link describeUserAgent}
 * turns a browser's `User-Agent` into "Safari on iPhone", and
 * {@link describeMoment} turns a stamp into "2 days ago". Both are
 * deliberately coarse — a device list is for recognizing your own phone
 * among three entries, not for forensics, and a reading that is right
 * nine times in ten and short beats one that is exact and unreadable.
 *
 * @packageDocumentation
 */

import type { DeviceSession, SignInDoor, SignInEvent } from '@orpheus/api-contracts';
import type { DevicesPanelLabels } from '@orpheus/player-ui';

/**
 * The panel's standing words.
 *
 * @remarks
 * **`revoke` stays "Sign out", the same word as the account's own button
 * above it** — asked and ruled (D171 §3). The repetition looks like a
 * defect and is not one: they are not two actions wanting two words, they
 * are ONE action aimed at different devices. This device has no button of
 * its own precisely because the account's Sign out is that button, so
 * calling the per-row one "End" or "Remove" would assert a difference
 * that does not exist.
 *
 * The alternatives are also each less true. "End" names nothing a player
 * has a model for — end what? "Remove" is a small lie: nothing is
 * removed, and the device reappears in this list the moment it signs in
 * again. The row already says which device; the button says what happens
 * to it, and every device list a player has ever used works this way.
 */
export const DEVICES_LABELS: DevicesPanelLabels = {
	heading: 'Signed in on',
	historyHeading: 'Recent sign-ins',
	revoke: 'Sign out',
	revokeOthers: 'Sign out everywhere else',
	thisDevice: 'this device',
};

/** What the shell says when a door refuses. */
export const DEVICES_NOTICE = {
	refused: {
		listDevices: 'Your devices could not be read just now.',
		revokeDevice: 'That device is still signed in.',
		revokeOthers: 'The other devices are still signed in.',
		/**
		 * D172: the identity could not be reached at all — the network's
		 * refusal, in the register rather than the browser's words.
		 *
		 * It says the devices could not be READ and not that they could not
		 * be REACHED, because nothing here reaches a device: the list is a
		 * table on our side, and "your devices could not be reached" invents
		 * a mechanism that does not exist and locates the fault on the
		 * player's phone rather than on us (§2.6 — name the limit actually
		 * doing the refusing). The only honest difference from `listDevices`
		 * is that a network that never answered is worth retrying, so that
		 * is the whole of what this adds.
		 */
		unreachable: 'Your devices could not be read just now. Try again in a moment.',
	},
	/** After ending the other sessions: how many went. */
	revokedOthers: (count: number) => (count === 1 ? 'Signed out on 1 other device.' : `Signed out on ${String(count)} other devices.`),
} as const;

/**
 * How each door is named in the history — total over the contract's list.
 *
 * @remarks
 * `other` is the door the log could not name — a provider added before
 * these labels caught up, or an old row. It reads "Another way" rather
 * than "Sign-in", because the door is the FIRST field of a line every one
 * of whose rows is a sign-in: "Sign-in · Safari on iPhone · 2 days ago"
 * spends the slot saying nothing and looks like a value that failed to
 * load. "Another way" says the true thing — a door, not one of the four
 * named — and the row's recognizing power was never in this field anyway;
 * it is in the browser, the place and the time.
 */
export const SIGN_IN_DOOR_LABELS: Readonly<Record<SignInDoor, string>> = {
	apple: 'Apple',
	google: 'Google',
	'magic-link': 'Email link',
	other: 'Another way',
	passkey: 'Passkey',
};

/** The browser families the reading knows, in the order that disambiguates them (Edge and Opera say "Chrome"; Chrome says "Safari"). */
const BROWSERS: readonly (readonly [RegExp, string])[] = [
	[/\bEdg(?:e|A|iOS)?\//u, 'Edge'],
	[/\bOPR\/|\bOpera\b/u, 'Opera'],
	[/\bFirefox\/|\bFxiOS\//u, 'Firefox'],
	[/\bChrome\/|\bCriOS\//u, 'Chrome'],
	[/\bSafari\//u, 'Safari'],
];

/** The platforms, likewise ordered (an iPad may say "Macintosh"; Android says "Linux"). */
const PLATFORMS: readonly (readonly [RegExp, string])[] = [
	[/\biPhone\b/u, 'iPhone'],
	[/\biPad\b/u, 'iPad'],
	[/\bAndroid\b/u, 'Android'],
	[/\bWindows\b/u, 'Windows'],
	[/\bMacintosh\b|\bMac OS X\b/u, 'Mac'],
	[/\bCrOS\b/u, 'ChromeOS'],
	[/\bLinux\b/u, 'Linux'],
];

/**
 * A browser on a platform, from a `User-Agent`: "Safari on iPhone",
 * "Chrome on Windows"; "A browser" when it says nothing recognizable.
 *
 * @param userAgent - The header as the browser sent it, if any
 * @returns The reading
 */
export const describeUserAgent = (userAgent: string | undefined): string => {
	if (userAgent === undefined || userAgent.length === 0) {
		return 'A browser';
	}

	const browser = BROWSERS.find(([pattern]) => pattern.test(userAgent))?.[1];
	const platform = PLATFORMS.find(([pattern]) => pattern.test(userAgent))?.[1];

	if (browser === undefined) {
		return platform === undefined ? 'A browser' : `A browser on ${platform}`;
	}

	return platform === undefined ? browser : `${browser} on ${platform}`;
};

/** A minute, an hour, a day, in milliseconds. */
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long ago a moment was, coarsely: "just now", "5 minutes ago",
 * "3 hours ago", "2 days ago", and a date past a month.
 *
 * @param iso - The moment
 * @param nowMs - Now, in milliseconds since the epoch
 * @returns The reading
 */
export const describeMoment = (iso: string, nowMs: number): string => {
	const elapsed = nowMs - Date.parse(iso);

	if (Number.isNaN(elapsed) || elapsed < MINUTE_MS) {
		return 'just now';
	}

	if (elapsed < HOUR_MS) {
		const minutes = Math.floor(elapsed / MINUTE_MS);

		return `${String(minutes)} minute${minutes === 1 ? '' : 's'} ago`;
	}

	if (elapsed < DAY_MS) {
		const hours = Math.floor(elapsed / HOUR_MS);

		return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`;
	}

	if (elapsed < 31 * DAY_MS) {
		const days = Math.floor(elapsed / DAY_MS);

		return `${String(days)} day${days === 1 ? '' : 's'} ago`;
	}

	return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** A device's second line: when it was last seen, and from where. */
export const deviceDetailOf = (device: DeviceSession, nowMs: number): string => {
	const seen = device.isCurrent ? 'Signed in' : `Last seen ${describeMoment(device.lastSeenAtIso, nowMs)}`;

	return device.ipAddress === undefined ? seen : `${seen} · ${device.ipAddress}`;
};

/** A sign-in's line: the door, the browser, the place, the moment. */
export const signInLineOf = (signIn: SignInEvent, nowMs: number): string => {
	const place = [signIn.city, signIn.country].filter((part): part is string => part !== undefined).join(', ');
	const parts = [SIGN_IN_DOOR_LABELS[signIn.door], describeUserAgent(signIn.userAgent), ...(place.length > 0 ? [place] : []), describeMoment(signIn.createdAtIso, nowMs)];

	return parts.join(' · ');
};
